import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store';
import { completeDispatch } from '../store/slices/dispatchSlice';
import { AlertTriangle, MapPin, CheckCircle, ChevronDown, Coffee, Car, Send, X, FileText, Plus } from 'lucide-react';
import { cn, getCategoryIcon, getSeverityBg, getSeverityColor, API_BASE_URL } from '../lib/utils';
import { useMobileContext } from '../layouts/MobileLayout';
import NavigationCompass from '../components/shared/NavigationCompass';
import MdtBreakRequestModal from '../components/mobile/MdtBreakRequestModal';
import BreakTimerDisplay from '../components/shared/BreakTimerDisplay';
import { io, Socket } from 'socket.io-client';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { enqueueAction } from '../lib/offlineQueue';
import { submitOrQueue } from '../lib/resilientApi';
import { getMdtEventId } from '../lib/mdtSessionStorage';
import { assignMdtResourceToIncident } from '../lib/mdtFormIncident';
import type { BreakRequest, BreakRequestType, ResourceBreak } from '../types';

function buildActiveBreakFromRequest(approved: BreakRequest): ResourceBreak {
  const since = approved.respondedAt || approved.requestedAt;
  const timerMode = approved.timerMode || 'countup';
  const base: ResourceBreak = {
    type: approved.type,
    typeLabel: approved.typeLabel,
    notes: approved.notes,
    commandNotes: approved.commandNotes,
    since,
    requestId: approved.id,
    timerMode,
  };
  if (timerMode === 'countdown' && approved.countdownMinutes) {
    base.countdownMinutes = approved.countdownMinutes;
    base.endsAt = new Date(new Date(since).getTime() + approved.countdownMinutes * 60 * 1000).toISOString();
  }
  return base;
}

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:4040';

type UnitStatus = 'available' | 'busy' | 'enroute' | 'onscene' | 'onbreak' | 'conveying';

const statusConfig: Record<UnitStatus, { label: string; color: string; dotColor: string; borderStyle: string }> = {
  available: { label: 'Available', color: 'text-emerald-400', dotColor: 'bg-emerald-400 shadow-[0_0_8px_#34d39960]', borderStyle: 'border-emerald-400/20 bg-emerald-400/[0.04]' },
  busy: { label: 'Busy', color: 'text-amber-400', dotColor: 'bg-amber-400 shadow-[0_0_8px_#fbbf2460]', borderStyle: 'border-amber-400/20 bg-amber-400/[0.04]' },
  enroute: { label: 'En Route', color: 'text-cyan-400', dotColor: 'bg-cyan-400 shadow-[0_0_8px_#22d3ee60]', borderStyle: 'border-cyan-400/20 bg-cyan-400/[0.04]' },
  onscene: { label: 'On Scene', color: 'text-red-400', dotColor: 'bg-red-400 shadow-[0_0_8px_#f8717160]', borderStyle: 'border-red-400/20 bg-red-400/[0.04]' },
  onbreak: { label: 'On Break', color: 'text-purple-400', dotColor: 'bg-purple-400 shadow-[0_0_8px_#c084fc60]', borderStyle: 'border-purple-400/20 bg-purple-400/[0.04]' },
  conveying: { label: 'Conveying', color: 'text-orange-400', dotColor: 'bg-orange-400 shadow-[0_0_8px_#fb923c60]', borderStyle: 'border-orange-400/20 bg-orange-400/[0.04]' },
};

interface ChatMsg {
  id: string;
  userId?: string;
  channel: string;
  channelType: string;
  targetUserId?: string;
  groupId?: string;
  from: string;
  fromName: string;
  text: string;
  timestamp: string;
  eventId?: string;
}

interface ChatChannel {
  id: string;
  type: 'command' | 'private' | 'group';
  name: string;
  targetUserId?: string;
  groupId?: string;
  resourceId?: string;
  resourceName?: string;
  isCommandStaff?: boolean;
}

function getChannelIdForMessage(msg: ChatMsg, currentUserId: string): string | null {
  if (msg.channelType === 'command') return 'command';
  if (msg.channelType === 'group' && msg.groupId) return `group:${msg.groupId}`;
  if (msg.channelType === 'private') {
    const senderId = msg.userId || msg.from;
    if (!senderId) return null;
    const peerId = senderId === currentUserId ? msg.targetUserId : senderId;
    if (peerId) return `private:${peerId}`;
  }
  return null;
}

function getChannelLabel(ch: ChatChannel): string {
  if (ch.type === 'command') return ch.name;
  if (ch.isCommandStaff) return `${ch.name} (Direct)`;
  if (ch.resourceName) return `${ch.name} · ${ch.resourceName}`;
  return ch.name;
}

export default function MobileDispatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const reduxDispatch = useAppDispatch();
  const { user, token } = useAppSelector((state) => state.auth);
  const { activeDispatch } = useAppSelector((state) => state.dispatch);
  const { unitStatus, setUnitStatus, chatOpen, setChatOpen, mdtResource } = useMobileContext();
  const [statusOpen, setStatusOpen] = useState(false);
  const [activeChannel, setActiveChannel] = useState<ChatChannel | null>(null);
  const [channels, setChannels] = useState<ChatChannel[]>([]);
  const [messagesByChannel, setMessagesByChannel] = useState<Record<string, ChatMsg[]>>({});
  const [unreadByChannel, setUnreadByChannel] = useState<Record<string, number>>({});
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const activeChannelRef = useRef<ChatChannel | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const [noteInput, setNoteInput] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [incidentLogs, setIncidentLogs] = useState<any[]>([]);
  const [urgentProgress, setUrgentProgress] = useState(0);
  const [emergencyActivated, setEmergencyActivated] = useState(false);
  const [quickActionFeedback, setQuickActionFeedback] = useState<string | null>(null);
  const [showConveyingModal, setShowConveyingModal] = useState(false);
  const [conveyingLocation, setConveyingLocation] = useState('');
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closingRemarks, setClosingRemarks] = useState('');
  const [submittingClose, setSubmittingClose] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const [showCompass, setShowCompass] = useState(false);
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [submittingBreak, setSubmittingBreak] = useState(false);
  const [pendingBreakRequest, setPendingBreakRequest] = useState<BreakRequest | null>(null);
  const [activeBreakRequestId, setActiveBreakRequestId] = useState<string | null>(null);
  const [activeBreak, setActiveBreak] = useState<ResourceBreak | null>(null);
  const [breakFeedback, setBreakFeedback] = useState<string | null>(null);
  const pressCountRef = useRef(0);
  const pressTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const acceptedAssignment = activeDispatch?.status === 'accepted' ? activeDispatch : null;

  useEffect(() => {
    const feedback = (location.state as { quickActionFeedback?: string } | null)?.quickActionFeedback;
    if (!feedback) return;
    setQuickActionFeedback(feedback);
    navigate('/', { replace: true, state: {} });
    const timer = setTimeout(() => setQuickActionFeedback(null), 5000);
    return () => clearTimeout(timer);
  }, [location.state, navigate]);

  // Urgent Assistance 3-press handler
  const handleUrgentPress = () => {
    pressCountRef.current++;
    setUrgentProgress((prev) => Math.min(prev + 33.33, 100));

    // Reset press count after 1 second
    if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
    pressTimeoutRef.current = setTimeout(() => {
      pressCountRef.current = 0;
      setUrgentProgress(0);
    }, 1000);

    // Trigger emergency after 3 presses
    if (pressCountRef.current >= 3) {
      if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
      pressCountRef.current = 0;
      triggerEmergency();
    }
  };

  // Calculate countdown display
  const getPressDisplay = () => {
    if (pressCountRef.current === 0) return 'Urgent Assist';
    if (pressCountRef.current >= 3) return 'Activated';
    return `${3 - pressCountRef.current}`;
  };


  const triggerEmergency = async () => {
    // Long vibration pattern for emergency
    if (navigator.vibrate) {
      navigator.vibrate([500, 200, 500, 200, 500, 200, 1000]);
    }

    // Play long emergency tone
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 880; // A5
    oscillator.type = 'sine';
    gainNode.gain.value = 0.5;

    oscillator.start();
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 2);
    oscillator.stop(audioContext.currentTime + 2);

    try {
      // Get GPS location
      let location = { latitude: 0, longitude: 0, address: 'Unknown' };
      if (navigator.geolocation) {
        const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 5000 });
        });

        // Reverse geocode to get address
        try {
          const geocodeRes = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&zoom=18&addressdetails=1`,
            { headers: { 'User-Agent': 'C2-Command-System' } }
          );
          if (geocodeRes.ok) {
            const geocodeData = await geocodeRes.json();
            const addr = geocodeData.address || {};
            const parts = [
              addr.road || addr.street || '',
              addr.suburb || addr.district || '',
              addr.town || addr.city || addr.village || '',
              addr.county || '',
              addr.postcode || ''
            ].filter(Boolean);
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              address: parts.length > 0
                ? `GPS Location Near: ${parts.join(', ')}`
                : `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
            };
          } else {
            location = {
              latitude: pos.coords.latitude,
              longitude: pos.coords.longitude,
              address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
            };
          }
        } catch {
          location = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            address: `${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`,
          };
        }
      }

      // Get event ID from session / resource
      const eventStr = localStorage.getItem('c2_mdt_event');
      const event = eventStr ? JSON.parse(eventStr) : null;
      const eventId = getMdtEventId(mdtResource) ?? event?.id ?? '';

      // Create emergency incident
      const incidentRes = await fetch(`${API_BASE_URL}/incidents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          eventId,
          title: `EMERGENCY - ${mdtResource?.name || 'Unit'} Urgent Assistance`,
          category: 'medical',
          severity: 'critical',
          status: 'open',
          location,
          description: `Emergency assistance requested by ${mdtResource?.name || 'Unit'}. Location: ${location.address}`,
        }),
      });

      if (incidentRes.ok) {
        const incidentData = await incidentRes.json();
        const incident = incidentData.incident;

        // Self-assign activating unit to the emergency incident
        if (mdtResource && eventId) {
          await assignMdtResourceToIncident({
            token,
            mdtResource,
            incident,
            eventId,
            location,
            reduxDispatch,
            resourceStatus: 'onscene',
            dispatchNotes: 'Emergency assistance (MDT self-assign)',
          });
          setUnitStatus('onscene');
        }

        // Add incident log
        await fetch(`${API_BASE_URL}/incidents/${incident.id}/logs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            details: `${mdtResource?.name || 'Unit'} activated emergency assistance via MDT`,
            action: 'Emergency Activated',
          }),
        });

        // Emit socket event to notify Command
        if (socketRef.current) {
          socketRef.current.emit('emergency:activated', {
            incidentId: incident.id,
            resourceId: mdtResource?.id,
            resourceName: mdtResource?.name,
            location,
          });
        }

        setEmergencyActivated(true);
        setTimeout(() => setEmergencyActivated(false), 5000);
      }
    } catch (error) {
      console.error('Emergency activation failed:', error);
    } finally {
      setUrgentProgress(0);
    }
  };

  // Load pending / active break state for this unit
  useEffect(() => {
    if (!mdtResource || !token) return;
    fetch(`${API_BASE_URL}/break-requests?resourceId=${encodeURIComponent(mdtResource.id)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        const list: BreakRequest[] = data.breakRequests || [];
        const pending = list.find((b) => b.status === 'pending') || null;
        const approved = list.find((b) => b.status === 'approved') || null;
        setPendingBreakRequest(pending);
        if (approved) {
          setActiveBreakRequestId(approved.id);
          setActiveBreak(buildActiveBreakFromRequest(approved));
          setUnitStatus('onbreak');
        }
      })
      .catch(() => {});
  }, [mdtResource?.id, token, setUnitStatus]);

  // Listen for break approval / decline / end
  useEffect(() => {
    if (!user || !mdtResource) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token: localStorage.getItem('c2_token'), userId: user.id },
    });

    const onResponded = (payload: { breakRequest: BreakRequest; action: string }) => {
      const br = payload.breakRequest;
      if (br.resourceId !== mdtResource.id) return;
      if (payload.action === 'approve') {
        setPendingBreakRequest(null);
        setActiveBreakRequestId(br.id);
        setActiveBreak(buildActiveBreakFromRequest(br));
        setUnitStatus('onbreak');
        setBreakFeedback(
          br.commandNotes
            ? `Break approved: ${br.typeLabel} — ${br.commandNotes}`
            : `Break approved: ${br.typeLabel}`
        );
      } else {
        setPendingBreakRequest(null);
        setBreakFeedback(br.declineReason ? `Break declined: ${br.declineReason}` : 'Break request declined');
      }
      setTimeout(() => setBreakFeedback(null), 5000);
    };

    const onEnded = (payload: { breakRequest: BreakRequest }) => {
      if (payload.breakRequest.resourceId !== mdtResource.id) return;
      setActiveBreakRequestId(null);
      setActiveBreak(null);
      setUnitStatus('available');
      setBreakFeedback('Break ended');
      setTimeout(() => setBreakFeedback(null), 4000);
    };

    socket.on('break:responded', onResponded);
    socket.on('break:ended', onEnded);
    return () => {
      socket.off('break:responded', onResponded);
      socket.off('break:ended', onEnded);
      socket.disconnect();
    };
  }, [user?.id, mdtResource?.id, setUnitStatus]);

  const submitBreakRequest = async (type: BreakRequestType, notes?: string) => {
    if (!mdtResource || !token) throw new Error('No unit selected');
    setSubmittingBreak(true);
    try {
      const eventStr = localStorage.getItem('c2_mdt_event');
      const event = eventStr ? JSON.parse(eventStr) : null;
      const res = await fetch(`${API_BASE_URL}/break-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          resourceId: mdtResource.id,
          eventId: event?.id,
          type,
          notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit break request');
      setPendingBreakRequest(data.breakRequest);
      setBreakFeedback('Break request sent to Command');
      setTimeout(() => setBreakFeedback(null), 4000);
    } finally {
      setSubmittingBreak(false);
    }
  };

  const endActiveBreak = async () => {
    if (!activeBreakRequestId || !token) return;
    try {
      await fetch(`${API_BASE_URL}/break-requests/${activeBreakRequestId}/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setActiveBreakRequestId(null);
      setActiveBreak(null);
      setUnitStatus('available');
    } catch { /* ignore */ }
  };

  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (pressTimeoutRef.current) clearTimeout(pressTimeoutRef.current);
    };
  }, []);

  // Determine display status based on assignment and unit status
  const displayStatus = pendingBreakRequest ? 'Break Pending' :
                        unitStatus === 'onbreak' ? 'On Break' :
                        !acceptedAssignment ? 'Available' :
                        unitStatus === 'enroute' ? 'En Route' :
                        unitStatus === 'onscene' ? 'On Scene' : 'Assigned';
  const displayColor = pendingBreakRequest ? 'text-purple-300' :
                       unitStatus === 'onbreak' ? 'text-purple-400' :
                       !acceptedAssignment ? 'text-emerald-400' :
                       unitStatus === 'enroute' ? 'text-cyan-400' :
                       unitStatus === 'onscene' ? 'text-red-400' : 'text-amber-400';
  const displayDotColor = pendingBreakRequest ? 'bg-purple-400 shadow-[0_0_8px_#c084fc60]' :
                          unitStatus === 'onbreak' ? 'bg-purple-400 shadow-[0_0_8px_#c084fc60]' :
                          !acceptedAssignment ? 'bg-emerald-400 shadow-[0_0_8px_#34d39960]' :
                          unitStatus === 'enroute' ? 'bg-cyan-400 shadow-[0_0_8px_#22d3ee60]' :
                          unitStatus === 'onscene' ? 'bg-red-400 shadow-[0_0_8px_#f8717160]' : 'bg-amber-400 shadow-[0_0_8px_#fbbf2460]';
  const cfg = statusConfig[unitStatus];

  const callSign = mdtResource?.name || user?.badgeNumber || 'A-001';
  const isOnline = useOnlineStatus();

  const emitStatusChange = useCallback((status: UnitStatus, statusLabel: string) => {
    if (!mdtResource || !acceptedAssignment) return;
    const payload = { resourceId: mdtResource.id, resourceName: mdtResource.name, incidentId: acceptedAssignment.incidentId, status, statusLabel };
    if (isOnline && socketRef.current) {
      socketRef.current.emit('mdt:status_change', payload);
    } else {
      enqueueAction('status_change', { resourceId: mdtResource.id, status });
    }
  }, [mdtResource, acceptedAssignment, isOnline]);

  const chatEventId = getMdtEventId(mdtResource);

  useEffect(() => {
    activeChannelRef.current = activeChannel;
  }, [activeChannel]);

  // Connect socket for real-time chat
  useEffect(() => {
    if (!user) return;
    const socket = io(SOCKET_URL, {
      transports: ['websocket'],
      auth: { token: localStorage.getItem('c2_token'), userId: user.id, username: user.firstName ? `${user.firstName} ${user.lastName}` : user.email },
    });
    socketRef.current = socket;

    socket.on('chat:new_message', (msg: ChatMsg) => {
      const channelId = getChannelIdForMessage(msg, user.id);
      if (!channelId) return;
      const senderId = msg.userId || msg.from;
      setMessagesByChannel((prev) => {
        const existing = prev[channelId] || [];
        if (existing.some((m) => m.id === msg.id)) return prev;
        return { ...prev, [channelId]: [...existing, msg] };
      });
      if (senderId !== user.id && activeChannelRef.current?.id !== channelId) {
        setUnreadByChannel((prev) => ({
          ...prev,
          [channelId]: (prev[channelId] || 0) + 1,
        }));
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!token) return;
    const params = chatEventId ? `?eventId=${encodeURIComponent(chatEventId)}` : '';
    fetch(`${API_BASE_URL}/chat/channels${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        const nextChannels = data.channels || [];
        setChannels(nextChannels);
        setActiveChannel((current) => {
          if (current && nextChannels.some((ch: ChatChannel) => ch.id === current.id)) return current;
          return nextChannels[0] || null;
        });
      })
      .catch(() => {});
  }, [token, chatEventId]);

  // Fetch messages for active channel
  useEffect(() => {
    if (!activeChannel || !token) return;
    const params = new URLSearchParams();
    params.append('type', activeChannel.type);
    if (activeChannel.type === 'private') params.append('targetUserId', activeChannel.targetUserId || '');
    if (activeChannel.type === 'group') params.append('groupId', activeChannel.groupId || '');
    if (chatEventId) params.append('eventId', chatEventId);

    fetch(`${API_BASE_URL}/chat?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        setMessagesByChannel((prev) => ({
          ...prev,
          [activeChannel.id]: data.messages || [],
        }));
      })
      .catch(() => {});
    setUnreadByChannel((prev) => ({ ...prev, [activeChannel.id]: 0 }));
  }, [activeChannel, token, chatEventId]);

  const channelMessages = activeChannel ? (messagesByChannel[activeChannel.id] || []) : [];

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [channelMessages, activeChannel, chatOpen]);

  // Fetch incident logs when assignment is accepted
  useEffect(() => {
    if (!acceptedAssignment || !token) {
      setIncidentLogs([]);
      return;
    }
    fetch(`${API_BASE_URL}/incidents/${acceptedAssignment.incidentId}/logs`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setIncidentLogs(data.logs || []))
      .catch(() => setIncidentLogs([]));
  }, [acceptedAssignment?.incidentId, token]);

  const sendMessage = async () => {
    const text = chatInput.trim();
    if (!text || !user || !activeChannel || !token) return;
    const payload = {
      channelType: activeChannel.type,
      targetUserId: activeChannel.targetUserId,
      groupId: activeChannel.groupId,
      text,
      eventId: chatEventId || undefined,
    };
    setChatInput('');
    if (isOnline && socketRef.current?.connected) {
      socketRef.current.emit('chat:message', payload);
      return;
    }
    const result = await submitOrQueue({
      method: 'POST',
      path: '/chat/messages',
      body: payload,
      token,
      queueType: 'chat_message',
      queuePayload: {
        channelType: activeChannel.type,
        targetUserId: activeChannel.targetUserId,
        groupId: activeChannel.groupId,
        text,
        eventId: chatEventId || undefined,
        from: user.id,
        fromName: user.firstName ? `${user.firstName} ${user.lastName}` : user.email,
      },
    });
    if (!result.ok && !result.queued) {
      setChatInput(text);
    }
  };

  const handleChatKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleAddNote = async () => {
    if (!noteInput.trim() || !acceptedAssignment || !token) return;
    setAddingNote(true);
    const noteText = noteInput.trim();
    setNoteInput('');
    try {
      const result = await submitOrQueue({
        method: 'POST',
        path: `/incidents/${acceptedAssignment.incidentId}/logs`,
        body: { details: noteText, action: 'Note', offlineQueued: true },
        token,
        queueType: 'incident_note',
        queuePayload: {
          incidentId: acceptedAssignment.incidentId,
          details: noteText,
          actionLabel: 'Note',
        },
      });
      if (result.ok || result.queued) {
        setIncidentLogs((prev) => [
          ...prev,
          {
            id: `local-${Date.now()}`,
            incidentId: acceptedAssignment.incidentId,
            userId: user?.id || '',
            userName: user?.firstName ? `${user.firstName} ${user.lastName}` : user?.email || 'You',
            action: 'Note',
            details: noteText,
            timestamp: new Date().toISOString(),
          },
        ]);
      }
    } catch { /* ignore */ } finally {
      setAddingNote(false);
    }
  };

  // ── Chat overlay ──
  if (chatOpen) {
    return (
      <div className="flex flex-col h-full">
        {/* Chat header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <span className="text-[15px] font-extrabold text-white">Messages</span>
          <button onClick={() => setChatOpen(false)}
            className="w-7 h-7 rounded-lg flex items-center justify-center"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <X className="w-3.5 h-3.5 text-white/50" />
          </button>
        </div>

        {/* Chat tabs */}
        <div className="flex gap-1 px-4 py-2 flex-shrink-0 overflow-x-auto"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          {channels.map((ch) => {
            const unread = unreadByChannel[ch.id] || 0;
            return (
              <button
                key={ch.id}
                onClick={() => {
                  setActiveChannel(ch);
                  setUnreadByChannel((prev) => ({ ...prev, [ch.id]: 0 }));
                }}
                className={cn(
                  'px-3.5 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5',
                  activeChannel?.id === ch.id
                    ? ch.type === 'private' ? 'bg-purple-500 text-white' : 'bg-blue-500 text-white'
                    : 'text-white/40 hover:text-white/60'
                )}
                style={activeChannel?.id !== ch.id ? { border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.03)' } : {}}
              >
                {getChannelLabel(ch)}
                {unread > 0 && activeChannel?.id !== ch.id && (
                  <span className="min-w-[14px] h-3.5 rounded-full bg-red-500 text-[8px] font-bold text-white flex items-center justify-center px-1">
                    {unread > 9 ? '9+' : unread}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1.5 min-h-0">
          <div className="text-center mb-3">
            <span className="text-[9px] text-white/25 px-3 py-1 rounded-lg"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}
            >
              {activeChannel?.type === 'private' ? 'Direct message' : 'Event-wide channel'} &middot; {getChannelLabel(activeChannel || { id: '', type: 'command', name: 'channel' })} &middot; Today
            </span>
          </div>
          {channelMessages.length === 0 && activeChannel && (
            <p className="text-center text-[10px] text-white/30 py-6">
              {activeChannel.type === 'private'
                ? 'No direct messages yet. Only messages in this thread appear here.'
                : 'No messages on the event channel yet.'}
            </p>
          )}
          {channelMessages.map((m) => {
            const isMe = m.from === user?.id;
            return (
              <div key={m.id} className={cn('max-w-[80%]', isMe ? 'ml-auto' : 'mr-auto')}>
                <div className={cn(
                  'px-3 py-2 text-[11px] leading-relaxed',
                  isMe
                    ? 'rounded-2xl rounded-br-sm bg-blue-500 text-white'
                    : 'rounded-2xl rounded-bl-sm text-white/90'
                )}
                  style={!isMe ? { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.04)' } : {}}
                >
                  <div className={cn('text-[8px] font-bold mb-1', isMe ? 'text-white/60' : 'text-blue-400')}>
                    {m.fromName}
                  </div>
                  {m.text}
                  <div className={cn('text-[7px] mt-1', isMe ? 'text-white/45' : 'text-white/25')}>
                    {new Date(m.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="flex gap-2 px-4 py-3 flex-shrink-0"
          style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}
        >
          <input type="text" placeholder={`Message ${getChannelLabel(activeChannel || { id: '', type: 'command', name: 'channel' })}...`}
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={handleChatKeyDown}
            disabled={!activeChannel}
            className="flex-1 px-4 py-2.5 rounded-full text-[12px] text-white/90 placeholder-white/30 outline-none disabled:opacity-40"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
          />
          <button onClick={sendMessage}
            disabled={!chatInput.trim() || !activeChannel}
            className={cn(
              'w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-opacity',
              chatInput.trim() && activeChannel ? 'bg-blue-500 opacity-100' : 'bg-blue-500/40 opacity-50'
            )}
          >
            <Send className="w-4 h-4 text-white" />
          </button>
        </div>
      </div>
    );
  }

  // ── Main dispatch page ──
  return (
    <div className="flex flex-col h-full">
      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto px-[18px] pb-4 min-h-0 space-y-4">

        {/* ── STATUS DISPLAY ── */}
        <div className="pt-2">
          <button onClick={() => {
            console.log('Status toggle clicked, current statusOpen:', statusOpen);
            setStatusOpen(!statusOpen);
          }}
            className="flex items-center gap-2.5 w-full"
          >
            <div className={cn('w-3 h-3 rounded-full flex-shrink-0 animate-pulse', displayDotColor)} />
            <span className={cn('text-[18px] font-extrabold flex-1 text-left', displayColor)}>
              {displayStatus}
            </span>
            <ChevronDown className={cn('w-4 h-4 text-white/30 transition-transform', statusOpen && 'rotate-180')} />
          </button>

          {/* Call sign */}
          <div className="flex items-center gap-2 mt-1 ml-[22px]">
            <span className="text-[16px] font-extrabold font-mono text-white/90">
              {callSign}
            </span>
            <span className="text-[9px] text-white/30">
              {mdtResource?.department || user?.department || 'Command'} &middot; {user?.firstName} {user?.lastName}
            </span>
          </div>
          {activeBreak && unitStatus === 'onbreak' && (
            <div className="ml-[22px] mt-2 flex flex-col gap-1">
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-purple-300 font-semibold">{activeBreak.typeLabel}</span>
                <BreakTimerDisplay activeBreak={activeBreak} compact />
              </div>
              {activeBreak.commandNotes && (
                <p className="text-[10px] text-white/45 italic">{activeBreak.commandNotes}</p>
              )}
            </div>
          )}
        </div>

        {/* STATUS SELECTOR (expandable) */}
        {statusOpen && (
          <div className="flex gap-[5px] animate-in slide-in-from-top-2 duration-200">
            {(Object.entries(statusConfig) as [UnitStatus, typeof cfg][])
              .filter(([key]) => key !== 'onbreak')
              .map(([key, c]) => (
              <button
                key={key}
                onClick={() => {
                  if (unitStatus === 'onbreak' && activeBreakRequestId) {
                    endActiveBreak();
                  }
                  setUnitStatus(key);
                  setStatusOpen(false);
                  emitStatusChange(key, c.label);
                }}
                className={cn(
                  'flex-1 py-2.5 rounded-[14px] text-center transition-all active:scale-93',
                  unitStatus === key
                    ? cn('border-[1.5px]', c.borderStyle)
                    : 'border-[1.5px] border-white/[0.06] bg-white/[0.02]'
                )}
              >
                <div className={cn('text-[7px] font-bold uppercase tracking-wide mt-0.5',
                  unitStatus === key ? c.color : 'text-white/30'
                )}>
                  {c.label}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* ── ASSIGNED INCIDENT ── */}
        <div>
          <p className="text-[8px] font-bold uppercase tracking-[1.5px] text-white/30 mb-2">
            Assigned Incident
          </p>
          {acceptedAssignment ? (
            <div className="glass-card overflow-hidden">
              {/* Severity bar */}
              <div className="flex items-center justify-between px-3.5 py-[7px]"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}
              >
                <span className={cn(
                  'text-[8px] font-extrabold uppercase tracking-wide px-2 py-[3px] rounded-md',
                  getSeverityBg(acceptedAssignment.incidentSeverity),
                  getSeverityColor(acceptedAssignment.incidentSeverity)
                )}>
                  {acceptedAssignment.incidentSeverity}
                </span>
                <span className="text-[9px] font-mono text-white/30">{acceptedAssignment.incidentId}</span>
              </div>
              <div className="p-3.5 space-y-2.5">
                <div className="flex items-start gap-2">
                  <span className="text-xl">{getCategoryIcon(acceptedAssignment.incidentCategory)}</span>
                  <p className="text-[15px] font-bold text-white/90 leading-tight flex-1">
                    {acceptedAssignment.incidentTitle}
                  </p>
                </div>
                <div className="flex items-start gap-2 p-2.5 rounded-xl"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.04)' }}
                >
                  <MapPin className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-medium text-white/80">
                      {acceptedAssignment.incidentLocation.address || 'Location pending'}
                    </p>
                    <p className="text-[9px] text-white/25 font-mono">
                      {acceptedAssignment.incidentLocation.latitude.toFixed(4)}, {acceptedAssignment.incidentLocation.longitude.toFixed(4)}
                    </p>
                  </div>
                </div>
                {acceptedAssignment.notes && (
                  <p className="text-[10px] text-white/50 p-2 rounded-xl"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.04)' }}
                  >
                    {acceptedAssignment.notes}
                  </p>
                )}
                <div className="flex gap-2 pt-1">
                  {unitStatus === 'enroute' ? (
                    <button onClick={() => {
                      setUnitStatus('onscene');
                      emitStatusChange('onscene', 'On Scene');
                    }}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.15)', color: '#f87171' }}
                    >
                      <MapPin className="w-3.5 h-3.5" /> On Scene
                    </button>
                  ) : unitStatus === 'onscene' ? (
                    <button onClick={() => setShowConveyingModal(true)}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(251,146,60,0.15)', border: '1px solid rgba(251,146,60,0.25)', color: '#fb923c' }}
                    >
                      <Car className="w-3.5 h-3.5" /> Conveying
                    </button>
                  ) : unitStatus === 'conveying' ? (
                    <button onClick={() => setShowConveyingModal(true)}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(251,146,60,0.25)', border: '1px solid rgba(251,146,60,0.4)', color: '#fb923c' }}
                    >
                      <Car className="w-3.5 h-3.5" /> Conveying
                    </button>
                  ) : (
                    <button onClick={() => {
                      setUnitStatus('enroute');
                      emitStatusChange('enroute', 'En Route');
                    }}
                      className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-transform"
                      style={{ background: 'rgba(68,237,170,0.12)', border: '1px solid rgba(68,237,170,0.15)', color: '#34d399' }}
                    >
                      En Route
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (unitStatus !== 'onscene' && unitStatus !== 'conveying') {
                        setQuickActionFeedback('Mark On Scene or Conveying before completing the job');
                        setTimeout(() => setQuickActionFeedback(null), 4000);
                        return;
                      }
                      setCloseError(null);
                      setShowClosingModal(true);
                    }}
                    className="flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition-all"
                    style={unitStatus === 'onscene' || unitStatus === 'conveying'
                      ? { background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }
                      : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.45)' }
                    }
                  >
                    <CheckCircle className="w-3 h-3" /> Done
                  </button>
                </div>

                {/* Closing remarks modal */}
                {showClosingModal && (
                  <div className="fixed inset-0 z-[200] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <div className="w-full max-w-md rounded-t-2xl p-5 space-y-4" style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[15px] font-extrabold text-white">Closing Remarks</p>
                        <button onClick={() => setShowClosingModal(false)}>
                          <X className="w-4 h-4 text-white/40" />
                        </button>
                      </div>
                      <p className="text-[11px] text-white/40">Add a handover note for the command team. This will be posted to the incident log before closing your assignment.</p>
                      {closeError && (
                        <p className="text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                          {closeError}
                        </p>
                      )}
                      <textarea
                        autoFocus
                        rows={4}
                        placeholder="e.g. Patient conveyed to Kings College, condition stable. Scene clear."
                        value={closingRemarks}
                        onChange={(e) => setClosingRemarks(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none resize-none"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                      />
                      <button
                        disabled={submittingClose}
                        onClick={async () => {
                          if (!acceptedAssignment || !token) return;
                          setSubmittingClose(true);
                          setCloseError(null);
                          try {
                            let completed = false;
                            let errorMessage = 'Failed to complete job';
                            const res = await fetch(`${API_BASE_URL}/dispatches/${acceptedAssignment.id}/complete`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ closingRemarks: closingRemarks.trim() }),
                            });
                            if (res.ok) {
                              completed = true;
                            } else {
                              const errBody = await res.json().catch(() => ({}));
                              errorMessage = errBody.error || errorMessage;
                              if (mdtResource) {
                                const fallback = await fetch(`${API_BASE_URL}/resources/${mdtResource.id}/complete-job`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                  body: JSON.stringify({
                                    incidentId: acceptedAssignment.incidentId,
                                    eventId: getMdtEventId(mdtResource),
                                    closingRemarks: closingRemarks.trim(),
                                  }),
                                });
                                if (fallback.ok) {
                                  completed = true;
                                } else {
                                  const fbErr = await fallback.json().catch(() => ({}));
                                  errorMessage = fbErr.error || errorMessage;
                                }
                              }
                            }
                            if (!completed) throw new Error(errorMessage);
                            reduxDispatch(completeDispatch(acceptedAssignment.id));
                            setUnitStatus('available');
                            if (socketRef.current && mdtResource) {
                              socketRef.current.emit('mdt:status_change', {
                                resourceId: mdtResource.id,
                                resourceName: mdtResource.name,
                                incidentId: acceptedAssignment.incidentId,
                                status: 'available',
                                statusLabel: 'Cleared / Available',
                              });
                            }
                            setShowClosingModal(false);
                            setClosingRemarks('');
                            setQuickActionFeedback('Job complete — you are available');
                            setTimeout(() => setQuickActionFeedback(null), 4000);
                          } catch (err) {
                            setCloseError(err instanceof Error ? err.message : 'Failed to complete job');
                            return;
                          } finally {
                            setSubmittingClose(false);
                          }
                        }}
                        className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95"
                        style={{ background: 'rgba(34,197,94,0.2)', border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e' }}
                      >
                        {submittingClose ? 'Submitting...' : 'Complete Job'}
                      </button>
                      <button
                        onClick={() => setShowClosingModal(false)}
                        className="w-full py-2.5 rounded-xl text-xs font-medium"
                        style={{ color: 'rgba(255,255,255,0.3)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Conveying location modal */}
                {showConveyingModal && (
                  <div className="fixed inset-0 z-[200] flex items-end justify-center" style={{ background: 'rgba(0,0,0,0.7)' }}>
                    <div className="w-full max-w-md rounded-t-2xl p-5 space-y-4" style={{ background: '#1a1d23', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <div className="flex items-center justify-between">
                        <p className="text-[15px] font-extrabold text-white">Conveying To</p>
                        <button onClick={() => setShowConveyingModal(false)}>
                          <X className="w-4 h-4 text-white/40" />
                        </button>
                      </div>
                      <p className="text-[11px] text-white/40">Enter the hospital or location you are conveying the patient to</p>
                      <input
                        autoFocus
                        type="text"
                        placeholder="e.g. Kings College Hospital"
                        value={conveyingLocation}
                        onChange={(e) => setConveyingLocation(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl text-sm text-white/90 placeholder-white/25 outline-none"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && conveyingLocation.trim()) {
                            setUnitStatus('conveying');
                            emitStatusChange('conveying', `Conveying to ${conveyingLocation.trim()}`);
                            setShowConveyingModal(false);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          if (!conveyingLocation.trim()) return;
                          setUnitStatus('conveying');
                          emitStatusChange('conveying', `Conveying to ${conveyingLocation.trim()}`);
                          setShowConveyingModal(false);
                        }}
                        disabled={!conveyingLocation.trim()}
                        className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-95"
                        style={conveyingLocation.trim()
                          ? { background: 'rgba(251,146,60,0.25)', border: '1px solid rgba(251,146,60,0.4)', color: '#fb923c' }
                          : { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.2)' }
                        }
                      >
                        Confirm Conveying
                      </button>
                    </div>
                  </div>
                )}
                {/* Incident logs/notes */}
                {incidentLogs.length > 0 && (
                  <div className="pt-2">
                    <p className="text-[9px] font-bold uppercase tracking-wider text-white/30 mb-2">Incident Notes</p>
                    <div className="space-y-1.5 max-h-32 overflow-y-auto">
                      {incidentLogs.map((log) => (
                        <div key={log.id} className="p-2 rounded-lg text-[10px]"
                          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="font-semibold text-white/70">{log.action}</span>
                            <span className="text-white/25">
                              {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                            </span>
                          </div>
                          <p className="text-white/50">{log.details}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Notes input */}
                <div className="pt-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Add note..."
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddNote(); } }}
                      className="flex-1 px-3 py-2 rounded-lg text-[11px] text-white/90 placeholder-white/30 outline-none"
                      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                    />
                    <button
                      onClick={handleAddNote}
                      disabled={addingNote || !noteInput.trim()}
                      className="px-3 py-2 rounded-lg flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
                      style={{ background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.2)', color: '#60a5fa' }}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card p-5">
              <div className="text-center text-white/30">
                <span className="text-2xl block mb-1 opacity-30">📋</span>
                <span className="text-[11px]">No active assignment</span>
                <br />
                <span className="text-[10px] text-white/15">Standing by for dispatch</span>
              </div>
            </div>
          )}
        </div>

        {/* ── ACTIONS ── */}
        <div>
          {breakFeedback && (
            <div className="mb-3 px-3 py-2 rounded-xl text-[11px] font-medium text-center"
              style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.2)', color: '#93c5fd' }}
            >
              {breakFeedback}
            </div>
          )}
          <p className="text-[8px] font-bold uppercase tracking-[1.5px] text-white/30 mb-2">
            Actions
          </p>
          {quickActionFeedback && (
            <div className="mb-3 px-3 py-2 rounded-xl text-[11px] font-medium text-center"
              style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.2)', color: '#93c5fd' }}
            >
              {quickActionFeedback}
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            {/* Urgent Assistance */}
            <button
              onClick={handleUrgentPress}
              className="py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform relative overflow-hidden"
              style={{ background: 'rgba(255,80,116,0.06)', border: '1.5px solid rgba(255,80,116,0.2)' }}
            >
              {/* Progress bar overlay */}
              {urgentProgress > 0 && (
                <div
                  className="absolute inset-0 bg-red-500/30 transition-all duration-100"
                  style={{ width: `${urgentProgress}%` }}
                />
              )}
              <AlertTriangle className="w-6 h-6 text-red-400 relative z-10" />
              <span className="text-[9px] font-bold text-red-400 uppercase tracking-wide relative z-10">
                {getPressDisplay()}
              </span>
            </button>
            {/* Request Break */}
            <button
              onClick={() => {
                if (pendingBreakRequest) return;
                if (activeBreakRequestId) {
                  endActiveBreak();
                  return;
                }
                setShowBreakModal(true);
              }}
              disabled={!!pendingBreakRequest}
              className={cn(
                'py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform',
                pendingBreakRequest && 'opacity-60 cursor-not-allowed'
              )}
              style={{ background: 'rgba(184,140,255,0.04)', border: '1.5px solid rgba(184,140,255,0.15)' }}
            >
              <Coffee className="w-6 h-6 text-purple-400" />
              <span className="text-[9px] font-bold text-purple-400 uppercase tracking-wide text-center px-1">
                {pendingBreakRequest
                  ? 'Break Pending'
                  : activeBreakRequestId
                  ? 'End Break'
                  : 'Request Break'}
              </span>
            </button>
            {/* Patient Report Form → type selection hub */}
            <button
              onClick={() => navigate('/forms')}
              className="col-span-2 py-4 rounded-[16px] flex flex-col items-center gap-1.5 active:scale-95 transition-transform"
              style={{ background: 'rgba(34,197,94,0.06)', border: '1.5px solid rgba(34,197,94,0.2)' }}
            >
              <FileText className="w-6 h-6 text-green-400" />
              <span className="text-[9px] font-bold text-green-400 uppercase tracking-wide">Patient Report Form</span>
            </button>
          </div>
        </div>


      </div>

      {/* Navigation Compass Modal */}
      {showCompass && acceptedAssignment?.incidentLocation?.latitude && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <NavigationCompass
            targetLat={acceptedAssignment.incidentLocation.latitude}
            targetLng={acceptedAssignment.incidentLocation.longitude}
            targetName={acceptedAssignment.incidentTitle}
            onClose={() => setShowCompass(false)}
            className="w-full max-w-sm"
          />
        </div>
      )}

      <MdtBreakRequestModal
        open={showBreakModal}
        onClose={() => setShowBreakModal(false)}
        onSubmit={submitBreakRequest}
        submitting={submittingBreak}
      />

      {/* Emergency Activation Overlay */}
      {emergencyActivated && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-red-500/90 backdrop-blur-md animate-in fade-in duration-300">
          <div className="text-center">
            <AlertTriangle className="w-20 h-20 text-white mx-auto mb-4 animate-pulse" />
            <h2 className="text-3xl font-extrabold text-white mb-2">EMERGENCY ACTIVATED</h2>
            <p className="text-white/80 text-sm">Incident created and resource assigned</p>
            <p className="text-white/60 text-xs mt-2">Command has been notified</p>
          </div>
        </div>
      )}
    </div>
  );
}
